import { useTheme } from "next-themes"
import { Toaster as Sonner, toast as sonnerToast } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

// Helper to detect if a message looks like binary data
function isBinaryData(message: string): boolean {
  if (!message || typeof message !== 'string') return false;
  // Check if it's a long string of numbers/commas (binary data stringified)
  if (message.length > 200 && /^[\d,\s\[\]":]+$/.test(message)) {
    return true;
  }
  // Check if it looks like a Uint8Array or similar
  if (message.includes('Uint8Array') || message.includes('ArrayBuffer')) {
    return true;
  }
  return false;
}

// Override ALL toast methods to filter binary data
const originalToastError = sonnerToast.error;
const originalToastInfo = sonnerToast.info;
const originalToastSuccess = sonnerToast.success;
const originalToastWarning = sonnerToast.warning;

function filterToast(message: any, data?: any): boolean {
  const messageStr = typeof message === 'string' ? message : String(message || '');
  const description = data?.description || '';
  const descStr = typeof description === 'string' ? description : String(description || '');
  
  // Filter out binary data - return true if should be filtered
  return isBinaryData(messageStr) || isBinaryData(descStr);
}

sonnerToast.error = (message: any, data?: any) => {
  if (filterToast(message, data)) {
    console.warn('[Filtered Binary Data Toast.error]', { message, data });
    return '' as any; // Return empty toast ID to prevent display
  }
  return originalToastError(message, data);
};

sonnerToast.info = (message: any, data?: any) => {
  if (filterToast(message, data)) {
    console.warn('[Filtered Binary Data Toast.info]', { message, data });
    return '' as any;
  }
  return originalToastInfo(message, data);
};

sonnerToast.success = (message: any, data?: any) => {
  if (filterToast(message, data)) {
    console.warn('[Filtered Binary Data Toast.success]', { message, data });
    return '' as any;
  }
  return originalToastSuccess(message, data);
};

sonnerToast.warning = (message: any, data?: any) => {
  if (filterToast(message, data)) {
    console.warn('[Filtered Binary Data Toast.warning]', { message, data });
    return '' as any;
  }
  return originalToastWarning(message, data);
};

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }

